import { config } from '../config';
import { Logger } from './logger';

type Payload = Record<string, unknown>;

/** base64url without padding, as AppSync Events expects for the `header-` sub-protocol. */
export function base64UrlJson(value: unknown): string {
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export interface RealtimeOptions {
  getToken: () => Promise<string>;
  onReconnected: () => void;
  onEvent: (payload: Payload) => void;
  /** Test seam. */
  createSocket?: (url: string, protocols: string[]) => WebSocket;
}

/**
 * Minimal AppSync Events WebSocket client (port of the Flutter RealtimeClient).
 *
 * Browsers cannot set headers on a WebSocket, so the Firebase token travels in the
 * `header-<base64url>` sub-protocol that AppSync Events defines for exactly this.
 * Connects to `wss://<realtime-host>/event/realtime`, sends `connection_init`,
 * subscribes to one channel, and reconnects with exponential backoff (1s to 30s).
 */
export class RealtimeClient {
  private readonly log = new Logger('Realtime');
  private socket: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private watchdog: ReturnType<typeof setTimeout> | undefined;
  private ackTimer: ReturnType<typeof setTimeout> | undefined;
  private channelPath: string | null = null;
  private attempt = 0;
  private closed = false;
  private everConnected = false;
  /** Bumped per connection so a late event from a torn-down socket is ignored. */
  private generation = 0;

  constructor(private readonly opts: RealtimeOptions) {}

  get isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  subscribe(channelPath: string) {
    this.channelPath = channelPath;
    this.closed = false;
    void this.connect();
  }

  private async connect() {
    if (this.closed || !this.channelPath) return;
    if (!config.appSyncRealtimeHost || !config.appSyncHttpHost) {
      this.log.warn('AppSync hosts not configured; realtime disabled');
      return;
    }
    clearTimeout(this.reconnectTimer);
    const gen = ++this.generation;
    try {
      const idToken = await this.opts.getToken();
      if (this.closed || gen !== this.generation) return;
      const auth = { Authorization: `Bearer ${idToken}`, host: config.appSyncHttpHost };
      const url = `wss://${config.appSyncRealtimeHost}/event/realtime`;
      const protocols = ['aws-appsync-event-ws', `header-${base64UrlJson(auth)}`];
      const socket = this.opts.createSocket ? this.opts.createSocket(url, protocols) : new WebSocket(url, protocols);
      this.socket = socket;

      socket.onopen = () => {
        if (gen !== this.generation) return;
        this.send({ type: 'connection_init' });
        this.ackTimer = setTimeout(() => {
          if (gen !== this.generation) return;
          this.log.warn('connection_ack timeout');
          this.teardownAndRetry();
        }, 10_000);
      };
      socket.onmessage = (ev) => {
        if (gen === this.generation) this.onMessage(String(ev.data), auth);
      };
      socket.onerror = () => {
        if (gen === this.generation) this.log.warn('socket error');
      };
      socket.onclose = () => {
        if (gen !== this.generation || this.closed) return;
        this.log.warn('disconnected');
        this.teardownAndRetry();
      };
    } catch (e) {
      this.log.warn('connect failed', { error: e });
      this.teardownAndRetry();
    }
  }

  private onMessage(raw: string, auth: Record<string, string>) {
    let msg: Payload;
    try {
      msg = JSON.parse(raw) as Payload;
    } catch (e) {
      this.log.warn('non-JSON frame', { error: e });
      return;
    }
    switch (msg.type) {
      case 'connection_ack': {
        clearTimeout(this.ackTimer);
        const timeoutMs = typeof msg.connectionTimeoutMs === 'number' ? msg.connectionTimeoutMs : 300_000;
        this.armWatchdog(timeoutMs);
        this.send({ type: 'subscribe', id: `player-${Date.now()}`, channel: this.channelPath, authorization: auth });
        this.log.debug('connection acknowledged, subscribe sent', { channel: this.channelPath });
        break;
      }
      case 'ka':
        this.armWatchdog(300_000);
        break;
      case 'subscribe_success': {
        // Backoff reset and the reconnect refresh happen here, so a rejected
        // subscription keeps backing off instead of hammering the API.
        const wasReconnect = this.everConnected;
        this.everConnected = true;
        this.attempt = 0;
        this.log.info('subscribed', { channel: this.channelPath, reconnect: wasReconnect });
        if (wasReconnect) this.opts.onReconnected();
        break;
      }
      case 'subscribe_error':
      case 'connection_error':
      case 'error':
        // Token may have expired; reconnecting fetches a fresh one.
        this.log.warn('server error frame', { frame: msg });
        this.teardownAndRetry();
        break;
      case 'data': {
        const event = msg.event;
        let payload: unknown = event;
        if (typeof event === 'string') {
          try {
            payload = JSON.parse(event);
          } catch {
            return;
          }
        }
        if (payload && typeof payload === 'object') this.opts.onEvent(payload as Payload);
        break;
      }
      default:
        break;
    }
  }

  private armWatchdog(timeoutMs: number) {
    clearTimeout(this.watchdog);
    this.watchdog = setTimeout(() => {
      this.log.warn('keep-alive timeout; reconnecting');
      this.teardownAndRetry();
    }, timeoutMs + 5_000);
  }

  private teardownAndRetry() {
    this.teardown();
    this.scheduleReconnect();
  }

  private scheduleReconnect() {
    if (this.closed) return;
    clearTimeout(this.reconnectTimer);
    const delay = Math.min(30, 1 << Math.min(this.attempt, 5));
    this.attempt++;
    this.log.info('reconnecting', { inSeconds: delay, attempt: this.attempt });
    this.reconnectTimer = setTimeout(() => void this.connect(), delay * 1000);
  }

  private send(frame: Payload) {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(frame));
  }

  private teardown() {
    this.generation++;
    clearTimeout(this.watchdog);
    clearTimeout(this.ackTimer);
    const s = this.socket;
    this.socket = null;
    if (s) {
      s.onopen = s.onmessage = s.onerror = s.onclose = null;
      try {
        s.close();
      } catch {
        /* already closed */
      }
    }
  }

  close() {
    this.closed = true;
    clearTimeout(this.reconnectTimer);
    this.teardown();
  }
}
